/**
 * Table block conformance — Zod schemas to validate and normalize vendor output
 * to canonical table → row → cell structure.
 *
 * Canonical:
 *   { type: 'table', children: [
 *     { type: 'row', children: [{ type: 'cell', value: '...' }] }
 *   ]}
 *
 * Non-conforming patterns we normalize:
 * 1. Docling flat cells: children with row/col grid indices, no row nesting
 * 2. Markdown value: pipe-delimited table string in `value`, no children
 * 3. HTML value: `<table>...</table>` string in `value`, no children
 */

import { Data, Effect, Schema, SchemaIssue } from 'effect';
import { z } from 'zod';
import {
  CanonicalBlockSchema,
  CanonicalPageSchema,
  type CanonicalBlock,
  type CanonicalPage,
} from './types';
import { resolveBlockType } from './formatters';

const CanonicalPagesInputSchema = Schema.mutable(Schema.Array(CanonicalPageSchema));

export type TableNormalizationOperation = 'validate' | 'normalize-block' | 'normalize-pages';
export type TableNormalizationIssue = {
  readonly path: ReadonlyArray<PropertyKey>;
};

/** Redacted table-boundary failure that never retains cells or raw parser payloads. */
export class TableNormalizationError extends Data.TaggedError('TableNormalizationError')<{
  readonly operation: TableNormalizationOperation;
  readonly issues: ReadonlyArray<TableNormalizationIssue>;
}> {}

const formatStandardSchemaIssue = SchemaIssue.makeFormatterStandardSchemaV1();

function tableNormalizationError(
  operation: TableNormalizationOperation,
  cause: unknown,
): TableNormalizationError {
  const issues: ReadonlyArray<TableNormalizationIssue> = Schema.isSchemaError(cause)
    ? formatStandardSchemaIssue(cause.issue).issues.map((issue) => ({
        path: issue.path?.map((segment) =>
          typeof segment === 'object' ? segment.key : segment,
        ) ?? [],
      }))
    : [{ path: [] }];
  return new TableNormalizationError({ operation, issues });
}

function decodeTableInputEffect<S extends Schema.ConstraintDecoder<unknown>>(
  operation: TableNormalizationOperation,
  schema: S,
  input: unknown,
): Effect.Effect<S['Type'], TableNormalizationError> {
  return Schema.decodeUnknownEffect(schema, { errors: 'all' })(input).pipe(
    Effect.mapError((cause) => tableNormalizationError(operation, cause)),
  );
}

// ── Zod schemas for canonical table structure ───────────────────────

const BboxSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
}).optional();

/** A cell MUST have a string value (even empty string is ok). */
const CellBlockSchema = z.object({
  type: z.literal('cell'),
  value: z.string(),
  label: z.string().optional(),
  bbox: BboxSchema,
  confidence: z.number().optional(),
});

/** A row MUST have at least one cell child. */
const RowBlockSchema = z.object({
  type: z.literal('row'),
  label: z.string().optional(),
  bbox: BboxSchema,
  confidence: z.number().optional(),
  children: z.array(CellBlockSchema).min(1),
});

/** A table MUST have at least one row child with cells. */
export const TableBlockSchema = z.object({
  type: z.literal('table'),
  label: z.string().optional(),
  value: z.string().optional(),
  bbox: BboxSchema,
  confidence: z.number().optional(),
  children: z.array(RowBlockSchema).min(1),
});

export type ValidTableBlock = z.infer<typeof TableBlockSchema>;

// ── Docling flat-cell schema (for normalization) ────────────────────

const DoclingCellSchema = z.object({
  text: z.string().optional(),
  value: z.string().optional(),
  content: z.string().optional(),
  start_row_offset_idx: z.number().optional(),
  end_row_offset_idx: z.number().optional(),
  start_col_offset_idx: z.number().optional(),
  end_col_offset_idx: z.number().optional(),
  row_index: z.number().optional(),
  col_index: z.number().optional(),
  rowIndex: z.number().optional(),
  columnIndex: z.number().optional(),
  row_span: z.number().optional(),
  col_span: z.number().optional(),
}).passthrough();

// ── Validation ──────────────────────────────────────────────────────

export interface TableConformanceIssue {
  pageNumber: number;
  blockIndex: number;
  zodErrors: z.ZodIssue[];
}

/**
 * Validate all table blocks in a parse result against the canonical Zod schema.
 * Returns issues found (empty array = all conforming).
 */
function validateTableBlocksUnsafe(pages: CanonicalPage[]): TableConformanceIssue[] {
  const issues: TableConformanceIssue[] = [];

  for (const page of pages) {
    for (let i = 0; i < page.blocks.length; i++) {
      const block = page.blocks[i];
      if (resolveBlockType(block.type) !== 'table') continue;

      const result = TableBlockSchema.safeParse(block);
      if (!result.success) {
        issues.push({
          pageNumber: page.pageNumber,
          blockIndex: i,
          zodErrors: result.error.issues,
        });
      }
    }
  }

  return issues;
}

/** Native validation boundary for parser-produced page arrays. */
export const validateTableBlocksEffect = Effect.fn('ParserSdkTables.validate')(
  function* (input: unknown) {
    yield* decodeTableInputEffect('validate', CanonicalPagesInputSchema, input);
    return validateTableBlocksUnsafe(input as CanonicalPage[]);
  },
);

/** Existing synchronous validator derived from the native program. */
export function validateTableBlocks(pages: CanonicalPage[]): TableConformanceIssue[] {
  return Effect.runSync(validateTableBlocksEffect(pages));
}

// ── Normalization ───────────────────────────────────────────────────

/**
 * Attempt to normalize a non-conforming table block into canonical form.
 * Returns the block unchanged if it already conforms or can't be fixed.
 */
function normalizeTableBlockUnsafe(block: CanonicalBlock): CanonicalBlock {
  if (resolveBlockType(block.type) !== 'table') return block;

  // Already conforms — fast path
  if (TableBlockSchema.safeParse(block).success) return block;

  // Pattern 1: Flat cells with grid coordinates (Docling / Azure DI style)
  if (block.children && block.children.length > 0) {
    const flatCells = tryParseFlatCells(block.children);
    if (flatCells.length > 0) {
      return { ...block, children: flatCellsToRows(flatCells) };
    }
  }

  // Pattern 2: Markdown pipe table in value
  if (block.value && block.value.includes('|') && !block.children?.length) {
    const parsed = parseMarkdownTable(block.value);
    if (parsed.length > 0) {
      return { ...block, children: parsed };
    }
  }

  // Pattern 3: HTML table in value
  if (block.value && block.value.includes('<table') && !block.children?.length) {
    const parsed = parseHtmlTable(block.value);
    if (parsed.length > 0) {
      return { ...block, children: parsed };
    }
  }

  return block;
}

/** Native normalization boundary for one parser-produced block. */
export const normalizeTableBlockEffect = Effect.fn('ParserSdkTables.normalizeBlock')(
  function* (input: unknown) {
    yield* decodeTableInputEffect('normalize-block', CanonicalBlockSchema, input);
    return normalizeTableBlockUnsafe(input as CanonicalBlock);
  },
);

/** Existing synchronous normalizer derived from the native program. */
export function normalizeTableBlock(block: CanonicalBlock): CanonicalBlock {
  return Effect.runSync(normalizeTableBlockEffect(block));
}

/**
 * Normalize all table blocks in a parse result. Mutates pages in-place.
 * Returns count of tables normalized.
 */
function normalizeParseResultTablesUnsafe(pages: CanonicalPage[]): number {
  let normalized = 0;

  for (const page of pages) {
    for (let i = 0; i < page.blocks.length; i++) {
      if (page.blocks[i].type !== 'table') continue;

      const before = page.blocks[i];
      const wasBroken = !TableBlockSchema.safeParse(before).success;

      if (wasBroken) {
        page.blocks[i] = normalizeTableBlockUnsafe(before);
        if (TableBlockSchema.safeParse(page.blocks[i]).success) {
          normalized++;
        }
      }
    }
  }

  return normalized;
}

/** Native in-place normalization boundary for parser-produced pages. */
export const normalizeParseResultTablesEffect = Effect.fn('ParserSdkTables.normalizePages')(
  function* (input: unknown) {
    yield* decodeTableInputEffect('normalize-pages', CanonicalPagesInputSchema, input);
    return normalizeParseResultTablesUnsafe(input as CanonicalPage[]);
  },
);

/** Existing synchronous in-place normalizer derived from the native program. */
export function normalizeParseResultTables(pages: CanonicalPage[]): number {
  return Effect.runSync(normalizeParseResultTablesEffect(pages));
}

// ── Helpers ─────────────────────────────────────────────────────────

interface ParsedFlatCell {
  text: string;
  rowIdx: number;
  colIdx: number;
}

function tryParseFlatCells(children: CanonicalBlock[]): ParsedFlatCell[] {
  const cells: ParsedFlatCell[] = [];
  for (const child of children) {
    const result = DoclingCellSchema.safeParse(child);
    if (!result.success) continue;

    const raw = result.data;
    const rowIdx = raw.start_row_offset_idx ?? raw.row_index ?? raw.rowIndex;
    const colIdx = raw.start_col_offset_idx ?? raw.col_index ?? raw.columnIndex;
    if (rowIdx === undefined || colIdx === undefined) continue;

    cells.push({
      text: raw.text ?? raw.value ?? raw.content ?? '',
      rowIdx,
      colIdx,
    });
  }
  return cells;
}

function flatCellsToRows(cells: ParsedFlatCell[]): CanonicalBlock[] {
  const rowMap = new Map<number, ParsedFlatCell[]>();
  for (const cell of cells) {
    if (!rowMap.has(cell.rowIdx)) rowMap.set(cell.rowIdx, []);
    rowMap.get(cell.rowIdx)!.push(cell);
  }

  const rows: CanonicalBlock[] = [];
  for (const [ri, rowCells] of [...rowMap.entries()].sort(([a], [b]) => a - b)) {
    const sorted = rowCells.sort((a, b) => a.colIdx - b.colIdx);
    rows.push({
      type: 'row',
      label: `Row ${ri}`,
      children: sorted.map(c => ({ type: 'cell' as const, value: c.text })),
    });
  }

  return rows;
}

function parseMarkdownTable(md: string): CanonicalBlock[] {
  const lines = md.split('\n').filter(l => l.trim().startsWith('|'));
  if (lines.length < 2) return [];

  const rows: CanonicalBlock[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    // Skip separator row (|---|---|)
    if (/^\|[\s-|]+\|$/.test(trimmed)) continue;

    const cells = trimmed
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(c => c.trim());

    rows.push({
      type: 'row',
      label: `Row ${rows.length}`,
      children: cells.map(c => ({ type: 'cell' as const, value: c })),
    });
  }

  return rows;
}

function parseHtmlTable(html: string): CanonicalBlock[] {
  const rows: CanonicalBlock[] = [];

  const trMatches = html.matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi);
  for (const trMatch of trMatches) {
    const cellMatches = trMatch[1].matchAll(/<(?:td|th)[^>]*>([\s\S]*?)<\/(?:td|th)>/gi);
    const cells: CanonicalBlock[] = [];
    for (const cellMatch of cellMatches) {
      const text = cellMatch[1].replace(/<[^>]*>/g, '').trim();
      cells.push({ type: 'cell', value: text });
    }
    if (cells.length > 0) {
      rows.push({
        type: 'row',
        label: `Row ${rows.length}`,
        children: cells,
      });
    }
  }

  return rows;
}
